// Debounced autosave for a drawer edited in place (finance-real, the user 2026-10-22: "it auto saves with debounce").
// frontend/src has no debounced-save hook, so this is one: each field's change waits AUTOSAVE_MS after the last
// keystroke, then saves, and a newer change to the same field supersedes an older one still waiting. The drawer reads
// one state for the platform's Saving… mark (BriefDetailView's, useIdeaDetailContent's) and each field its refusal, which
// stays under the field with the user's value kept until the next change. A change still waiting when the drawer closes
// is saved then, not lost.
import { useCallback, useEffect, useRef, useState } from "react";

export const AUTOSAVE_MS = 600;

export type AutosaveState = "idle" | "saving" | "saved";

interface Pending<F extends string> {
  field: F;
  run: () => Promise<unknown>;
}

export function useFieldAutosave<F extends string>() {
  const timers = useRef(new Map<F, number>());
  const waiting = useRef(new Map<F, Pending<F>>());
  // The latest save sent for each field: an older answer never overwrites a newer one's outcome.
  const sent = useRef(new Map<F, number>());
  const inFlight = useRef(0);
  const mounted = useRef(true);
  const [state, setState] = useState<AutosaveState>("idle");
  const [errors, setErrors] = useState<Partial<Record<F, string>>>({});

  const setError = useCallback((field: F, message: string | null) => {
    if (!mounted.current) return;
    setErrors((e) => {
      const next = { ...e };
      if (message === null) delete next[field];
      else next[field] = message;
      return next;
    });
  }, []);

  const send = useCallback(
    (p: Pending<F>) => {
      const n = (sent.current.get(p.field) ?? 0) + 1;
      sent.current.set(p.field, n);
      inFlight.current += 1;
      if (mounted.current) setState("saving");
      const settle = (message: string | null) => {
        inFlight.current -= 1;
        if (sent.current.get(p.field) === n) setError(p.field, message);
        if (mounted.current && inFlight.current === 0 && waiting.current.size === 0) setState(message === null ? "saved" : "idle");
      };
      p.run().then(
        () => settle(null),
        (e: unknown) => settle(e instanceof Error && e.message ? e.message : "Not saved"),
      );
    },
    [setError],
  );

  /** Saves `run` for `field` once its changes stop for AUTOSAVE_MS. */
  const schedule = useCallback(
    (field: F, run: () => Promise<unknown>) => {
      const timer = timers.current.get(field);
      if (timer !== undefined) window.clearTimeout(timer);
      waiting.current.set(field, { field, run });
      setError(field, null);
      setState("saving");
      timers.current.set(
        field,
        window.setTimeout(() => {
          timers.current.delete(field);
          const p = waiting.current.get(field);
          waiting.current.delete(field);
          if (p) send(p);
        }, AUTOSAVE_MS),
      );
    },
    [send, setError],
  );

  /** Drops a change still waiting for `field` (one the field itself finds invalid), and shows `message` under it. */
  const hold = useCallback(
    (field: F, message: string) => {
      const timer = timers.current.get(field);
      if (timer !== undefined) window.clearTimeout(timer);
      timers.current.delete(field);
      waiting.current.delete(field);
      setError(field, message);
      if (inFlight.current === 0 && waiting.current.size === 0) setState("idle");
    },
    [setError],
  );

  /** Clears `field`'s refusal, once what it was about has changed on the server. */
  const clear = useCallback((field: F) => setError(field, null), [setError]);

  // Closing the drawer saves what is still waiting.
  useEffect(() => {
    const t = timers.current;
    const w = waiting.current;
    mounted.current = true;
    return () => {
      mounted.current = false;
      t.forEach((timer) => window.clearTimeout(timer));
      w.forEach((p) => void p.run().catch(() => undefined));
    };
  }, []);

  return { schedule, hold, clear, state, errors };
}
